import {
  compact,
  concat,
  endsWith,
  every,
  filter,
  find,
  findIndex,
  first,
  flatMap,
  flatten,
  groupBy,
  includes,
  isEmpty,
  isNumber,
  isString,
  join,
  keys,
  map,
  partition,
  replace,
  slice,
  some,
  sortBy,
  split,
  startsWith,
  toNumber,
  uniqBy,
  values,
} from 'es-toolkit/compat';

import {
  ExperimentResult,
  FlowClassName,
  OptionsEntry,
  PresentationInfoLayer,
  publishableFlowClasses,
  publishableResultTypes,
  ResultType,
} from 'interfaces/experimentResults';
import { MAX_UINT8 } from 'utils/constants';
import {
  bracketsRegex,
  getClusterIdReassignmentForOrchestration,
  getDictBrackets,
  getFormattedCluster,
  isBrackets,
} from 'utils/formatBrackets';
import { FormatBracketsOptions } from 'utils/formatBrackets/formatBracketsOptions';
import {
  formatBracketsVisualization,
  getVisualizationId,
  visualizationIndexRegex,
} from 'utils/formatBrackets/formatBracketsVisualization';
import { humanize } from 'utils/helpers';
import { getRnaCountColumnDisplayName, parseRnaCountColumn } from 'utils/multiplex/rnaCountColumns';
import { markerPositivityColumnPrefix } from 'utils/parquetWorker/constants';
import { getScoreColor } from 'utils/scoreOverlays';
import { getCellSegmentationOverlayIdentity, resolveParsedCellSegmentation } from './cellSegmentationCompartment';
import {
  experimentResultToGroup,
  FeatureMetadata,
  FeatureMetadataBeforeSecondaryAnalysisGrouping,
  HeatmapType,
  ParquetLayerType,
  parseBaseFeatureMetadataFromExperimentResult,
  ResultOverlaySection,
} from './featureMetadata';
import {
  composeSecondaryHeatmapsFromPrimary,
  getPrimaryRunOrchestrationIdFromExperimentResult,
  groupPrimaryResultsWithSecondaryResults,
} from './secondaryAnalysis';
import { getSegmentationDefaultColor } from './segmentationOverlayDefaults';

export interface HeatmapParsingCache {
  primaryPayloads: WeakMap<ExperimentResult, FeatureMetadataBeforeSecondaryAnalysisGrouping[]>;
  onExperimentResultParsed?: (result: ExperimentResult) => void;
  onPrimaryPayloadParsed?: (result: ExperimentResult) => void;
  onPrimaryPayloadReused?: (result: ExperimentResult) => void;
}

export interface ParsedExperimentResultHeatmaps {
  result: ExperimentResult;
  heatmaps: FeatureMetadataBeforeSecondaryAnalysisGrouping[];
}

export interface ExperimentResultHeatmapParsingOptions {
  formatBracketsOptions: Omit<FormatBracketsOptions, 'orchestrationId'>;
  isMultiStain: boolean;
  skipVectorHeatmaps?: boolean;
  parsingCache?: HeatmapParsingCache;
}

export const experimentResultIdPrefixRegex = /^\[([a-zA-Z0-9-]+)\]-/;
export const stripExperimentResultIdFromHeatmapId = (heatmapId: string): string =>
  // Remove the experiment result id prefix from the heatmap id
  replace(heatmapId, experimentResultIdPrefixRegex, '');

export const getExperimentResultFromHeatmapId = (heatmapId: string): number | undefined => {
  const match = heatmapId.match(experimentResultIdPrefixRegex);
  const matchAsNumber = match && Number(match[1]);
  return match && isNumber(matchAsNumber) && !isNaN(matchAsNumber) ? matchAsNumber : undefined;
};

const extensionRegex = /\.\S+$/g;
const underscoreAndPathSepRegex = /[_/]/g;
const underscoreRegex = /_/g;

const sourceStainIndexRegex = /<source_s:s:(\d+)>/;

const consumeParsingGenerator = <T,>(generator: Generator<void, T, void>): T => {
  let iteration = generator.next();
  while (!iteration.done) {
    iteration = generator.next();
  }
  return iteration.value;
};

export const coordinateColumns = ['X', 'Y'] as const;
export const labelColumnName = 'label';
export const areaRegionColumnName = 'area_region';
export const clusterIdColumnName = 'cluster_id';
export const classificationPredictionClassIdName = 'classification.prediction_class_id';
export { markerPositivityColumnPrefix } from 'utils/parquetWorker/constants';
export const markerProbabilityColumnPrefix = 'marker.prob.';
const instanceCountPositivityActionName = 'get_instance_count_positivity_by_thresholding';

const getHeatmapSourceIdentity = (heatmap: FeatureMetadataBeforeSecondaryAnalysisGrouping) =>
  `${heatmap.experimentResultId}:${JSON.stringify(heatmap.heatmapUrl)}`;

const getInstanceCountPositivityDetails = (
  columnName: string,
  experimentResult: ExperimentResult,
  context: Pick<FormatBracketsOptions, 'stainTypeOptions' | 'rnaProbeDisplayNameOverridesByProbeId'>
) => {
  const match = parseRnaCountColumn(columnName);
  if (!match || match.representation !== 'pos') {
    return null;
  }

  const { probeId: instanceName, compartment, dilation } = match;
  const params = experimentResult.job?.params as {
    postprocessing?: Array<{
      actionName?: string;
      action_name?: string;
      action_params?: { thresholds_by_instance?: Record<string, number> };
      actionParams?: { thresholdsByInstance?: Record<string, number> };
    }>;
  };
  const positivityAction = find(
    params?.postprocessing || [],
    (action) => (action.actionName || action.action_name) === instanceCountPositivityActionName
  );
  const threshold =
    positivityAction?.action_params?.thresholds_by_instance?.[instanceName] ??
    positivityAction?.actionParams?.thresholdsByInstance?.[instanceName];
  const instanceDisplayName = context.rnaProbeDisplayNameOverridesByProbeId?.[instanceName] || humanize(instanceName);
  const details = compact([
    isNumber(threshold) ? `≥ ${threshold} counts` : null,
    humanize(compartment),
    dilation ? `${dilation} µm dilation` : null,
  ]);

  return {
    displayName: `${instanceDisplayName} Positive${details.length ? ` (${join(details, ', ')})` : ''}`,
    probeId: instanceName,
    probeDisplayName: instanceDisplayName,
    compartment,
    dilation: Number(dilation),
    threshold,
  };
};

export const quantificationColumnRegex =
  /^marker.([^.]+).geometry.(nuclear|membrane|cytoplasm).quant.(mean_intensity|od)/;

export const getQuantificationPropsFromColumnName = (columnName: string) => {
  const regexMatches = quantificationColumnRegex.exec(columnName);
  return {
    marker: regexMatches?.[1],
    geometry: regexMatches?.[2] as 'nuclear' | 'membrane' | 'cytoplasm',
    quantification: regexMatches?.[3] as 'mean_intensity' | 'od',
  };
};

const getBinnedScoreDetails = (columnName: string, context: FormatBracketsOptions) => {
  if (!endsWith(columnName, '.binned')) {
    return undefined;
  }
  const countColumn = parseRnaCountColumn(columnName);
  const quantification = getQuantificationPropsFromColumnName(columnName);
  const markerId = countColumn?.probeId || quantification.marker;
  if (!markerId) {
    return undefined;
  }
  const markerDisplayName =
    context.rnaProbeDisplayNameOverridesByProbeId?.[markerId] ||
    find(context.stainTypeOptions, { id: markerId })?.displayName ||
    humanize(markerId);
  const scoreContext = countColumn
    ? `${humanize(countColumn.compartment)} - ${countColumn.dilation}um dilation`
    : humanize(quantification.geometry);
  return {
    markerId,
    markerDisplayName,
    scoreContext,
    displayName: countColumn
      ? `${markerDisplayName} score (${countColumn.dilation}um dilation)`
      : `${markerDisplayName} ${quantification.geometry} score`,
  };
};

const getBinnedScoreOptions = (option: OptionsEntry): OptionsEntry[] => {
  const storedScores = (option.layers || []).filter(
    (layer) =>
      (typeof layer.key === 'number' || typeof layer.key === 'string') &&
      `${layer.key}`.trim() !== '' &&
      Number.isSafeInteger(Number(layer.key)) &&
      Number(layer.key) >= 0
  );
  const canUseRange =
    Number.isSafeInteger(option.min) &&
    Number.isSafeInteger(option.max) &&
    option.min >= 0 &&
    option.max >= option.min &&
    option.max - option.min <= MAX_UINT8;
  const layers: OptionsEntry[] = storedScores.length
    ? sortBy(storedScores, (layer) => Number(layer.key))
    : canUseRange
    ? Array.from({ length: option.max - option.min + 1 }, (_, index) => ({ key: option.min + index }))
    : [];
  return map(layers, (layer) => ({
    ...layer,
    color: getScoreColor({ options: layers }, layer.key),
  }));
};

export const getQuantificationSingleStainColumnGrouping = (columnName: string): string => {
  const quantificationProps = getQuantificationPropsFromColumnName(columnName || '');
  // Group single stains by their geometry alone
  return quantificationProps.quantification;
};

export const getQuantificationMultiStainColumnGrouping = (columnName: string): string => {
  const quantificationProps = getQuantificationPropsFromColumnName(columnName || '');
  // Group multiple stains for each geometry + quantification combo
  return (
    quantificationProps.geometry &&
    quantificationProps.quantification &&
    `${quantificationProps.quantification}.${quantificationProps.geometry}`
  );
};

export const markerPositivityColumnToDisplayName = (
  columnName: string,
  context: Pick<FormatBracketsOptions, 'stainTypeOptions'>
): string => {
  const stainType = replace(columnName, markerPositivityColumnPrefix, '');
  return find(context.stainTypeOptions, (option) => option.id === stainType)?.displayName || humanize(stainType);
};

export const markerProbabilityColumnToDisplayName = (
  columnName: string,
  context: Pick<FormatBracketsOptions, 'stainTypeOptions'>
): string => {
  const stainType = replace(columnName, markerProbabilityColumnPrefix, '');
  return find(context.stainTypeOptions, (option) => option.id === stainType)?.displayName || humanize(stainType);
};

export const quantificationToDisplayName = (quantification: 'od' | 'mean_intensity'): string =>
  quantification === 'mean_intensity'
    ? 'Mean Intensity'
    : quantification === 'od'
    ? 'Optical Density'
    : humanize(quantification);

export const quantificationHeatmapColumnGroupDisplayName = (
  columnName: string,
  isMultiStain: boolean,
  context: Pick<FormatBracketsOptions, 'stainTypeOptions'>
): string => {
  const { marker, geometry, quantification } = getQuantificationPropsFromColumnName(columnName || '');

  const stainTypeDisplayName =
    find(context.stainTypeOptions, (option) => option.id === marker)?.displayName || humanize(marker);

  return isMultiStain
    ? `${humanize(geometry)} ${quantificationToDisplayName(quantification)}`
    : `${quantificationToDisplayName(quantification)} (${stainTypeDisplayName})`;
};

export const quantificationColumnToDisplayName = (
  columnName: string,
  isMultiStain: boolean,
  context: Pick<FormatBracketsOptions, 'stainTypeOptions'>
): string => {
  const { marker, geometry } = getQuantificationPropsFromColumnName(columnName || '');

  const stainTypeDisplayName =
    find(context.stainTypeOptions, (option) => option.id === marker)?.displayName || humanize(marker);

  return isMultiStain ? stainTypeDisplayName : `${humanize(geometry)}`;
};

/**
 * Until we standardize the presentation info schema, we need to handle different types of heatmaps
 */
export const getPresentationInfoForOptionKey = (experimentResult: ExperimentResult, optionKey: string | number) => {
  return (experimentResult.presentationInfo?.[optionKey] ||
    experimentResult.presentationInfo?.layers?.[optionKey] ||
    find(experimentResult.presentationInfo?.layers, { key: optionKey }) ||
    find(experimentResult.presentationInfo?.layers, { class_name: optionKey })) as
    | PresentationInfoLayer
    | string
    | undefined;
};

/**
 * Generates a human-readable display name for heatmaps.
 *
 * This function handles various types of heatmap identifiers and converts them into
 * user-friendly names that will be shown in the UI. It supports multiple formats
 * including bracket notation, area visualizations, cell visualizations, and fallback
 * scenarios.
 *
 * @param context - Configuration options containing stain types, visualization types,
 *                  area types, taxonomy mappings, and UI settings
 * @param key - The identifier for the heatmap (could be bracket notation, number, or string)
 * @param mainHeatmapKey - Optional parent/main heatmap key for nested items
 * @param jobName - Optional job name (not passed for nested items)
 * @returns A human-readable display name for the heatmap
 */
export const getHeatmapDisplayName = (
  context: FormatBracketsOptions,
  key: string | number,
  mainHeatmapKey?: string,
  jobName?: string
) => {
  if (!isNumber(key) && isBrackets(key)) {
    return formatBracketsVisualization(key, context, mainHeatmapKey);
  }

  if (mainHeatmapKey) {
    const mainHeatmapKeyVisualizationMatch = mainHeatmapKey.match(visualizationIndexRegex);
    if (mainHeatmapKeyVisualizationMatch && !isNumber(key)) {
      const [, mainHeatmapKeyVisualizationIndex] = mainHeatmapKeyVisualizationMatch;

      const visualizationTypeOption = find(
        context.visualizationTypeOptions,
        (option) => option.index === +mainHeatmapKeyVisualizationIndex
      );

      if (visualizationTypeOption?.input === 'area') {
        const areaType = find(context.areaTypeOptions, (areaTypeOption) => areaTypeOption.id === key);
        if (areaType?.displayName) {
          return areaType.displayName;
        }
      } else if (visualizationTypeOption?.input === 'cell') {
        const { taxonomyTags, taxonomyIdToDisplayNameMap } = context;
        const [cellTypeId, tags] = split(key, '-');
        const formattedTags = map(split(tags, '_'), (tagId) => {
          const tagName = find(taxonomyTags, (taxonomyTag) => taxonomyTag.id === tagId)?.displayName;
          return tagName ? (tagName === tagId ? humanize(tagName) : tagName) : humanize(tagId);
        });

        if (taxonomyIdToDisplayNameMap[cellTypeId] || tags) {
          return `${taxonomyIdToDisplayNameMap[cellTypeId] ?? humanize(cellTypeId)} ${
            tags ? join(formattedTags, ' ') : ''
          }`;
        }
      }
    }
  }

  if (!isNumber(key) && context.rnaProbeDisplayNameOverridesByProbeId?.[`${key}`]) {
    return context.rnaProbeDisplayNameOverridesByProbeId[`${key}`];
  }

  const countColumnLabel = getRnaCountColumnDisplayName(
    `${key}`,
    (identifier) => context.rnaProbeDisplayNameOverridesByProbeId?.[identifier] || identifier
  );
  if (countColumnLabel) {
    return countColumnLabel;
  }

  // legacy name
  const itemConfig = context?.uiSettings?.webappSidebarConfig?.heatmapsConfig?.[key];

  return itemConfig?.displayName || jobName || replace(`${key}`, underscoreRegex, ' ');
};

const legacyPmtCompositeKey = 'composite_layer_info';

const isIntermediateCellSegmentationResult = (experimentResult: ExperimentResult) =>
  experimentResult.resultType === ResultType.SlideInferenceCellSegmentation &&
  experimentResult.flowClassName !== FlowClassName.CalculateFeatures;

function* parseSinglePmtHeatmapGenerator({
  experimentResult,
  presentationInfo,
  key,
  formatBracketsOptions,
}: {
  experimentResult: ExperimentResult;
  presentationInfo?: PresentationInfoLayer;
  key: string;
  formatBracketsOptions: FormatBracketsOptions;
}): Generator<void, FeatureMetadataBeforeSecondaryAnalysisGrouping | null, void> {
  if (presentationInfo?.file_type && presentationInfo.file_type !== 'pmt') {
    console.warn('Unsupported file type for pmt heatmap', { presentationInfo, experimentResult });
  }
  const pmtHeatmapUrl = presentationInfo?.storage_url;
  if (!pmtHeatmapUrl) {
    console.warn('PMT heatmap url not found', { presentationInfo, experimentResult });
  }

  const layerOptions = find(experimentResult.options, { key })?.layers;
  const optionsForHeatmap = layerOptions ? layerOptions : legacyPmtCompositeKey === key ? experimentResult.options : [];

  const registeredFromStainTypeIndex = sourceStainIndexRegex.test(key)
    ? toNumber(sourceStainIndexRegex.exec(key)[1])
    : undefined;

  const heatmapKeyWithoutRegisteredStain = isNumber(registeredFromStainTypeIndex)
    ? replace(key, sourceStainIndexRegex, '')
    : key;

  const visualizationParts = slice(split(key, bracketsRegex), 1, -1);
  const visualizationId = getVisualizationId(visualizationParts);
  const dilation = getDictBrackets(visualizationParts)['dilation'];
  const segmentationDilation = dilation === undefined ? undefined : Number(dilation);

  const nestedItems: FeatureMetadataBeforeSecondaryAnalysisGrouping[] = [];
  for (const heatmapOption of optionsForHeatmap) {
    const matchingLayer = getPresentationInfoForOptionKey(experimentResult, heatmapOption.key) as PresentationInfoLayer;
    if (matchingLayer && matchingLayer.file_type !== 'pmt') {
      if (key !== legacyPmtCompositeKey) {
        console.warn('Unsupported file type for PMT heatmap', { matchingLayer, experimentResult, heatmapOption });
      }
    } else {
      nestedItems.push({
        ...parseBaseFeatureMetadataFromExperimentResult(experimentResult, 'parsePmtHeatmaps-nested-'),
        id: `[${experimentResult.experimentResultId}]-parsePmtHeatmaps-nested-${heatmapOption.key}`,
        displayName: getHeatmapDisplayName(
          { ...formatBracketsOptions, orchestrationId: experimentResult?.orchestrationId },
          heatmapOption.key,
          heatmapKeyWithoutRegisteredStain
          // don't pass job name for nested items
        ),
        heatmapType: HeatmapType.PmtLayer,
        options: optionsForHeatmap,
        key: `${heatmapOption.key ?? ''}`,
        registeredFromStainTypeIndex,
        visualizationId,
      });
    }
    yield;
  }

  const baseFeatureMetadata = parseBaseFeatureMetadataFromExperimentResult(
    experimentResult,
    `parseSinglePmtHeatmap-${key}`
  );
  const displayNameWithoutRegistration =
    heatmapKeyWithoutRegisteredStain !== legacyPmtCompositeKey
      ? getHeatmapDisplayName(
          { ...formatBracketsOptions, orchestrationId: experimentResult?.orchestrationId },
          heatmapKeyWithoutRegisteredStain,
          heatmapKeyWithoutRegisteredStain,
          experimentResult?.job?.name
        )
      : baseFeatureMetadata?.displayName ||
        // If the experiment result name is not available, use the artifact url as the name by replacing the file extension and underscores with spaces
        replace(replace(presentationInfo?.artifact_url, extensionRegex, ''), underscoreAndPathSepRegex, ' ');

  const stainTypeIdDisplayName = isNumber(registeredFromStainTypeIndex)
    ? find(formatBracketsOptions?.stainTypeOptions, { index: registeredFromStainTypeIndex })?.displayName
    : null;
  const parsedCellSegmentation = resolveParsedCellSegmentation({
    isIntermediate: isIntermediateCellSegmentationResult(experimentResult),
    key,
    visualizationId,
    layerKeys: map(optionsForHeatmap, 'key'),
    visualizationTypeOptions: formatBracketsOptions.visualizationTypeOptions,
  });
  return {
    ...baseFeatureMetadata,
    displayName: `${
      stainTypeIdDisplayName
        ? // If the heatmap is registered from a stain, we will display it with the stain name
          `${displayNameWithoutRegistration} from ${stainTypeIdDisplayName}`
        : displayNameWithoutRegistration
    }`,
    heatmapUrl: pmtHeatmapUrl,
    heatmapType: HeatmapType.Pmt,
    nestedItems,
    options: optionsForHeatmap,
    registeredFromStainTypeIndex,
    visualizationId,
    overlaySection: parsedCellSegmentation.overlaySection,
    segmentationCompartment: parsedCellSegmentation.segmentationCompartment,
    segmentationDilation: Number.isFinite(segmentationDilation) ? segmentationDilation : undefined,
  } as FeatureMetadataBeforeSecondaryAnalysisGrouping;
}

const parseSinglePmtHeatmap = (
  options: Parameters<typeof parseSinglePmtHeatmapGenerator>[0]
): FeatureMetadataBeforeSecondaryAnalysisGrouping | null =>
  consumeParsingGenerator(parseSinglePmtHeatmapGenerator(options));

export const parseGeoJsonHeatmap = (
  experimentResult: ExperimentResult,
  layerInfo: PresentationInfoLayer,
  formatBracketsOptions: FormatBracketsOptions
): FeatureMetadataBeforeSecondaryAnalysisGrouping | null => {
  const geoJsonUrl = layerInfo?.storage_url;
  if (!geoJsonUrl) {
    console.warn('Dynamic heatmap url not found', { layerInfo, experimentResult });
    return null;
  }
  if (layerInfo?.file_type && layerInfo.file_type !== 'geojson') {
    console.warn('Unsupported file type for geojson heatmap', { layerInfo, experimentResult });
    return null;
  }

  const optionsForHeatmap = find(experimentResult.options, { key: layerInfo.key })?.layers || experimentResult.options;
  const matchingOption = find(optionsForHeatmap, { key: layerInfo.class_name });

  // We support his direct presentation info parsing since this is a legacy feature anyway
  const layerIndex = findIndex(experimentResult?.presentationInfo?.layers, { class_name: layerInfo.class_name });

  if (!matchingOption && layerIndex === -1) {
    console.warn('No matching option found for geojson layer', { layerInfo, experimentResult });
    return null;
  }

  const displayName =
    getHeatmapDisplayName(
      formatBracketsOptions,
      matchingOption?.key || layerInfo?.class_name,
      experimentResult?.job?.name
    ) ||
    // If the experiment result name is not available, use the artifact url as the name by replacing the file extension and underscores with spaces
    `${replace(replace(layerInfo?.artifact_url, extensionRegex, ''), underscoreAndPathSepRegex, ' ')}`;
  return {
    ...parseBaseFeatureMetadataFromExperimentResult(experimentResult, 'parseGeoJsonHeatmap-'),
    id: `[${experimentResult.experimentResultId}]-parseGeoJsonHeatmap=geojson-${layerInfo.class_name}`,
    key:
      `${matchingOption?.key ?? ''}` ||
      layerInfo?.class_name ||
      `[${experimentResult.experimentResultId}]-${layerIndex}`,
    displayName,
    heatmapUrl: geoJsonUrl,
    heatmapType: HeatmapType.GeoJson,
  };
};

function* parsePmtHeatmapsGenerator(
  experimentResult: ExperimentResult,
  formatBracketsOptions: FormatBracketsOptions
): Generator<void, FeatureMetadataBeforeSecondaryAnalysisGrouping[], void> {
  // TODO: we should align the options schema to have the nested layers in an option.layers array, making the source of truth the options array
  // This will then become the 'legacy' parser for pmt (if we decide to keep it)
  const pmtLayerKeys = concat(
    map(filter(experimentResult?.presentationInfo?.layers, { file_type: 'pmt' }), 'key'),
    filter(
      keys(experimentResult?.presentationInfo),
      (key) => experimentResult?.presentationInfo[key]?.file_type === 'pmt'
    )
  );

  const parsedHeatmaps: FeatureMetadataBeforeSecondaryAnalysisGrouping[] = [];
  for (const pmtLayerKey of pmtLayerKeys) {
    const presentationInfo = getPresentationInfoForOptionKey(experimentResult, pmtLayerKey) as PresentationInfoLayer;
    if (presentationInfo) {
      const parsedHeatmap = yield* parseSinglePmtHeatmapGenerator({
        experimentResult,
        presentationInfo,
        key: pmtLayerKey,
        formatBracketsOptions,
      });
      if (parsedHeatmap) {
        parsedHeatmaps.push(parsedHeatmap);
      }
    }
    yield;
  }
  const deduplicatedHeatmaps = uniqBy(
    parsedHeatmaps,
    (heatmap) => getCellSegmentationOverlayIdentity(heatmap) ?? heatmap
  );
  return map(deduplicatedHeatmaps, (heatmap) => {
    if (heatmap.overlaySection !== ResultOverlaySection.CellSegmentation) {
      return heatmap;
    }

    const segmentationDefaultColor = getSegmentationDefaultColor(heatmap, deduplicatedHeatmaps);
    return {
      ...heatmap,
      options: map(heatmap.options, (option) => ({
        ...option,
        color: `${option.key}` !== 'cell' && isString(option.color) ? option.color : segmentationDefaultColor,
      })),
    };
  });
}

export const parsePmtHeatmaps = (
  experimentResult: ExperimentResult,
  formatBracketsOptions: FormatBracketsOptions
): FeatureMetadataBeforeSecondaryAnalysisGrouping[] =>
  consumeParsingGenerator(parsePmtHeatmapsGenerator(experimentResult, formatBracketsOptions));

function* parseLegacyRasterHeatmapsGenerator(
  experimentResult: ExperimentResult
): Generator<void, FeatureMetadataBeforeSecondaryAnalysisGrouping[], void> {
  if (!experimentResult) {
    return [];
  }
  const legacyHeatmapPresentationInfo = getPresentationInfoForOptionKey(experimentResult, 'heatmap_url');

  if (isEmpty(legacyHeatmapPresentationInfo)) {
    return [];
  }

  const baseFeatureMetadata = parseBaseFeatureMetadataFromExperimentResult(
    experimentResult,
    'parseLegacyRasterHeatmaps-'
  );

  const parentDisplayName = baseFeatureMetadata?.displayName || humanize(experimentResult.flowClassName);
  const nestedItems: FeatureMetadataBeforeSecondaryAnalysisGrouping['nestedItems'] = [];
  for (const heatmapOption of experimentResult.options) {
    nestedItems.push({
      id: `[${experimentResult.experimentResultId}]-parseLegacyRasterHeatmaps-${heatmapOption.key}`,
      key: `${heatmapOption.key ?? ''}`,
      color: heatmapOption.color,
      displayName: `${heatmapOption?.key ?? ''}`,
      heatmapOption,
    });
    yield;
  }

  const newHMResult = {
    ...baseFeatureMetadata,
    displayName: parentDisplayName,
    heatmapUrl:
      (legacyHeatmapPresentationInfo as PresentationInfoLayer)?.storage_url ||
      (legacyHeatmapPresentationInfo as string),
    heatmapType: HeatmapType.Dzi,
    nestedItems,
  } as FeatureMetadataBeforeSecondaryAnalysisGrouping;

  return [newHMResult];
}

const parseLegacyRasterHeatmaps = (
  experimentResult: ExperimentResult
): FeatureMetadataBeforeSecondaryAnalysisGrouping[] =>
  consumeParsingGenerator(parseLegacyRasterHeatmapsGenerator(experimentResult));

/*
 * Get all the heatmap 'options' and parse their nested layers as composite raster heatmaps
 */
function* parseCompositeRasterHeatmapUrlsGenerator(
  experimentResult: ExperimentResult,
  formatBracketsOptions: FormatBracketsOptions
): Generator<void, FeatureMetadataBeforeSecondaryAnalysisGrouping[], void> {
  const compositeRasterHeatmaps: Array<FeatureMetadataBeforeSecondaryAnalysisGrouping | null> = [];
  for (const heatmapOption of experimentResult.options) {
    const compositePresentationInfo = heatmapOption
      ? getPresentationInfoForOptionKey(experimentResult, heatmapOption.key)
      : null;

    const baseFeatureMetadata = parseBaseFeatureMetadataFromExperimentResult(
      experimentResult,
      `parseCompositeRasterHeatmapUrls-${heatmapOption.key}`
    );

    const parentDisplayName =
      getHeatmapDisplayName(formatBracketsOptions, heatmapOption.key, experimentResult?.job?.name) ||
      baseFeatureMetadata?.displayName;

    const nestedItems: FeatureMetadataBeforeSecondaryAnalysisGrouping['nestedItems'] = [];
    for (const nestedHeatmap of heatmapOption.layers || []) {
      const presentationInfo =
        getPresentationInfoForOptionKey(experimentResult, `${heatmapOption.key}`.concat(`${nestedHeatmap.key}`)) ||
        getPresentationInfoForOptionKey(experimentResult, nestedHeatmap.key);
      const hasHeatmapUrl = presentationInfo && isString(presentationInfo);
      nestedItems.push({
        id: `[${experimentResult.experimentResultId}]-parseCompositeRasterHeatmapUrls-${heatmapOption.key}-${nestedHeatmap.key}`,
        key: `${heatmapOption.key ?? ''}`,
        color: nestedHeatmap.color,
        heatmapUrl: hasHeatmapUrl ? presentationInfo : undefined,
        displayName: getHeatmapDisplayName(formatBracketsOptions, nestedHeatmap.key, `${heatmapOption.key}`),
        heatmapType: HeatmapType.Dzi,
        options: heatmapOption.layers,
        heatmapOption,
      });
      yield;
    }

    const hasCompositeHeatmapUrl = compositePresentationInfo && isString(compositePresentationInfo);

    // Some heatmaps don't have either the composite heatmap url or nested heatmap urls, but if both are missing, we should log a warning and skip the heatmap
    if (!hasCompositeHeatmapUrl && !some(nestedItems, 'heatmapUrl')) {
      compositeRasterHeatmaps.push(null);
      yield;
      continue;
    }

    compositeRasterHeatmaps.push({
      ...baseFeatureMetadata,
      id: `[${experimentResult.experimentResultId}]-parseCompositeRasterHeatmapUrls-${heatmapOption.key}`,
      key: `${heatmapOption.key ?? ''}`,
      color: heatmapOption.color,
      heatmapUrl: hasCompositeHeatmapUrl ? compositePresentationInfo : undefined,
      heatmapType: HeatmapType.Dzi,
      nestedItems,
      displayName: parentDisplayName,
      options: heatmapOption.layers,
      heatmapOption,
    });
    yield;
  }

  return filter(
    compositeRasterHeatmaps,
    // Only return heatmaps that have a heatmapUrl or nested items with heatmapUrls that are unique across all composite raster heatmaps
    (compositeRasterHeatmap) =>
      compositeRasterHeatmap &&
      (compositeRasterHeatmap.heatmapUrl ||
        (!isEmpty(compositeRasterHeatmap.nestedItems) &&
          !some(
            compositeRasterHeatmaps,
            (otherHeatmap) =>
              otherHeatmap !== compositeRasterHeatmap &&
              Boolean(otherHeatmap?.heatmapUrl) &&
              every(compositeRasterHeatmap.nestedItems, (nestedItem) =>
                some(otherHeatmap.nestedItems, { heatmapUrl: nestedItem.heatmapUrl })
              )
          )))
  ) as FeatureMetadataBeforeSecondaryAnalysisGrouping[];
}

const parseCompositeRasterHeatmapUrls = (
  experimentResult: ExperimentResult,
  formatBracketsOptions: FormatBracketsOptions
): FeatureMetadataBeforeSecondaryAnalysisGrouping[] =>
  consumeParsingGenerator(parseCompositeRasterHeatmapUrlsGenerator(experimentResult, formatBracketsOptions));

/*
 * Get all the heatmap 'options' and parse their nested layers as composite raster heatmaps
 */
function* parseParquetHeatmapsGenerator(
  experimentResult: ExperimentResult,
  formatBracketsOptions: FormatBracketsOptions,
  isMultiStain: boolean,
  debug: boolean = false
): Generator<void, FeatureMetadataBeforeSecondaryAnalysisGrouping[], void> {
  const parsedHeatmaps: FeatureMetadataBeforeSecondaryAnalysisGrouping[] = [];
  for (const heatmapOption of experimentResult.options) {
    if (heatmapOption?.type === 'point' || includes(coordinateColumns, heatmapOption.key)) {
      // Point columns are the X, Y coordinates of cells, not data for a heatmap
      yield;
      continue;
    }
    const presentationInfo = getPresentationInfoForOptionKey(experimentResult, heatmapOption.key);
    if (!presentationInfo || isString(presentationInfo) || presentationInfo.file_type !== 'parquet') {
      yield;
      continue;
    }
    const columnName = heatmapOption.column_name;
    if (!columnName) {
      console.warn('No column name found for parquet heatmap', { heatmapOption, experimentResult });
      yield;
      continue;
    }
    if (columnName === 'uuid') {
      // The uuid column is not a heatmap, just the id of the cell
      yield;
      continue;
    }
    const columnType = heatmapOption?.type;

    // Check for problematic options
    const isMarkerPositivity = startsWith(columnName, markerPositivityColumnPrefix);
    if (isMarkerPositivity && columnType && !includes(['boolean', 'categorical'], columnType)) {
      if (debug) {
        // To avoid spamming the console, only log this in debug mode
        console.warn('Marker positivity column is not boolean or categorical, this could be a bug', {
          columnName,
          columnType,
          heatmapOption,
          experimentResult,
        });
      }
    }
    const isMarkerProbability = startsWith(columnName, markerProbabilityColumnPrefix);
    const scoreDetails = getBinnedScoreDetails(columnName, formatBracketsOptions);
    const isQuantificationColumn = !scoreDetails && quantificationColumnRegex.test(columnName);
    if ((isMarkerProbability || isQuantificationColumn) && columnType !== 'float') {
      if (debug) {
        // To avoid spamming the console, only log this in debug mode
        console.warn('Marker probability or quantification column is not float. Skipping to avoid bugs', {
          columnName,
          columnType,
          heatmapOption,
        });
      }
      yield;
      continue;
    }

    const instanceCountPositivityDetails = getInstanceCountPositivityDetails(
      columnName,
      experimentResult,
      formatBracketsOptions
    );
    const isCellClassification =
      columnName === labelColumnName || Boolean(instanceCountPositivityDetails) || Boolean(scoreDetails);
    const isCellClassificationPredictionClassId = columnName === classificationPredictionClassIdName;
    const isAreaRegionClassification = columnName === areaRegionColumnName;
    const isClusterIdClassification = endsWith(columnName, clusterIdColumnName);

    const registeredFromStainTypeIndex =
      !isNumber(heatmapOption.key) && sourceStainIndexRegex.test(heatmapOption.key)
        ? toNumber(sourceStainIndexRegex.exec(heatmapOption.key)[1])
        : undefined;

    const parquetLayerType = isQuantificationColumn
      ? ParquetLayerType.Quantification
      : isMarkerPositivity
      ? ParquetLayerType.MarkerPositivity
      : isMarkerProbability
      ? ParquetLayerType.MarkerProbability
      : isCellClassification
      ? isNumber(registeredFromStainTypeIndex)
        ? ParquetLayerType.CellClassificationDensities
        : ParquetLayerType.CellClassification
      : isCellClassificationPredictionClassId
      ? ParquetLayerType.CellIntensityClassification
      : isAreaRegionClassification
      ? ParquetLayerType.AreaRegionClassification
      : isClusterIdClassification
      ? ParquetLayerType.ClusterIdClassification
      : ParquetLayerType.Other;

    const heatmapKey = isQuantificationColumn
      ? isMultiStain
        ? `${heatmapOption.key}.${getQuantificationMultiStainColumnGrouping(columnName)}`
        : `${heatmapOption.key}.${getQuantificationSingleStainColumnGrouping(columnName)}`
      : isMarkerPositivity
      ? `${heatmapOption.key}.${markerPositivityColumnPrefix}`
      : isMarkerProbability
      ? `${heatmapOption.key}.${markerProbabilityColumnPrefix}`
      : isCellClassification ||
        isCellClassificationPredictionClassId ||
        isAreaRegionClassification ||
        isClusterIdClassification
      ? `${heatmapOption.key}.${columnName}`
      : heatmapOption.key;

    const heatmapKeyWithoutRegisteredStain =
      isNumber(registeredFromStainTypeIndex) && !isNumber(heatmapKey)
        ? replace(heatmapKey, sourceStainIndexRegex, '')
        : heatmapKey;

    const baseFeatureMetadata = parseBaseFeatureMetadataFromExperimentResult(
      experimentResult,
      `parseParquetHeatmaps-${heatmapKeyWithoutRegisteredStain}`
    );

    const displayNameWithoutRegistration = scoreDetails
      ? scoreDetails.displayName
      : instanceCountPositivityDetails
      ? 'Positive Cells'
      : isQuantificationColumn
      ? // TODO: support grouping the quantification, marker positivity and marker probability columns in the parser. Currently handled in the viewer
        quantificationColumnToDisplayName(columnName, isMultiStain, formatBracketsOptions)
      : isMarkerPositivity
      ? markerPositivityColumnToDisplayName(columnName, formatBracketsOptions)
      : isMarkerProbability
      ? markerProbabilityColumnToDisplayName(columnName, formatBracketsOptions)
      : isAreaRegionClassification
      ? 'Area Region'
      : isClusterIdClassification
      ? 'Neighborhood'
      : // Cell classification requires additional context and will be handled in the viewer
        getHeatmapDisplayName(formatBracketsOptions, heatmapKeyWithoutRegisteredStain, experimentResult?.job?.name);

    const visualizationParts = slice(split(`${heatmapOption.key}`, bracketsRegex), 1, -1);
    const visualizationId = getVisualizationId(visualizationParts);

    const nestedOptions = scoreDetails
      ? getBinnedScoreOptions(heatmapOption)
      : instanceCountPositivityDetails
      ? [{ key: 'true', color: first(heatmapOption?.layers)?.color }]
      : heatmapOption?.layers;

    const orchestrationClusterIdReassignments =
      formatBracketsOptions.studyClusterIdReassignments && formatBracketsOptions.orchestrationId
        ? formatBracketsOptions.studyClusterIdReassignments[formatBracketsOptions.orchestrationId]
        : undefined;
    const clusterIdReassignmentsForOrchestration = getClusterIdReassignmentForOrchestration({
      orchestrationReassignmentOptions: orchestrationClusterIdReassignments,
    });

    const nestedItems: FeatureMetadataBeforeSecondaryAnalysisGrouping['nestedItems'] = [];
    for (const nestedHeatmap of nestedOptions || []) {
      nestedItems.push({
        ...baseFeatureMetadata,
        id: `[${experimentResult.experimentResultId}]-parseParquetHeatmaps-${heatmapKey}-${nestedHeatmap.key}`,
        key: `${heatmapKey}-${nestedHeatmap.key}`,
        color: nestedHeatmap.color,
        displayName: scoreDetails
          ? `Score ${nestedHeatmap.key}`
          : instanceCountPositivityDetails
          ? instanceCountPositivityDetails.displayName
          : isCellClassificationPredictionClassId && isNumber(Number(nestedHeatmap.key))
          ? `Cell intensity ${Number(nestedHeatmap.key) + 1}`
          : isClusterIdClassification
          ? getFormattedCluster(
              `${nestedHeatmap.key}`,
              formatBracketsOptions.clusterTypeOptions,
              clusterIdReassignmentsForOrchestration
            )
          : getHeatmapDisplayName(formatBracketsOptions, nestedHeatmap.key, `${heatmapKeyWithoutRegisteredStain}`),
        heatmapType: HeatmapType.Parquet,
        options: nestedOptions,
        columnName,
        columnType,
        heatmapOption,
        registeredFromStainTypeIndex,
        parquetLayerType,
        visualizationId,
      });
      yield;
    }

    if (isCellClassification && !scoreDetails && isEmpty(nestedItems)) {
      if (debug) {
        console.warn("Cell classification heatmap doesn't have nested items, this is a bug in the data or the parser", {
          heatmapOption,
          experimentResult,
        });
      }
      yield;
      continue;
    }

    const stainTypeIdDisplayName = isNumber(registeredFromStainTypeIndex)
      ? find(formatBracketsOptions?.stainTypeOptions, { index: registeredFromStainTypeIndex })?.displayName
      : null;

    const { quantification } = getQuantificationPropsFromColumnName(columnName || '');

    const newHMResult: FeatureMetadataBeforeSecondaryAnalysisGrouping = {
      ...baseFeatureMetadata,
      id: `[${experimentResult.experimentResultId}]-parseParquetHeatmaps-${heatmapKey}`,
      key: `${heatmapKey ?? ''}`,
      color: heatmapOption.color,
      heatmapUrl: presentationInfo.storage_url,
      heatmapType: HeatmapType.Parquet,
      nestedItems,
      displayName: `${
        stainTypeIdDisplayName
          ? // If the heatmap is registered from a stain, we will display it as a density heatmap with the stain name
            `${displayNameWithoutRegistration} ${
              isCellClassification && heatmapOption?.type === 'categorical' ? 'density ' : ''
            }from ${stainTypeIdDisplayName}`
          : displayNameWithoutRegistration
      }`,
      options: nestedOptions,
      columnName,
      min: isNumber(heatmapOption.min) ? heatmapOption.min : quantification === 'od' ? 0 : undefined,
      max: isNumber(heatmapOption.max) ? heatmapOption.max : quantification === 'od' ? MAX_UINT8 : undefined,
      columnType: heatmapOption?.type,
      heatmapOption,
      registeredFromStainTypeIndex,
      parquetLayerType,
      visualizationId,
      overlaySection: scoreDetails
        ? ResultOverlaySection.Scores
        : instanceCountPositivityDetails
        ? ResultOverlaySection.SpotPositivity
        : undefined,
      positivityProbeId: instanceCountPositivityDetails?.probeId,
      positivityProbeDisplayName: instanceCountPositivityDetails?.probeDisplayName,
      positivityCompartment: instanceCountPositivityDetails?.compartment,
      positivityDilation: instanceCountPositivityDetails?.dilation,
      positivityThreshold: instanceCountPositivityDetails?.threshold,
      scoreMarkerId: scoreDetails?.markerId,
      scoreMarkerDisplayName: scoreDetails?.markerDisplayName,
      scoreContext: scoreDetails?.scoreContext,
    };

    const hasUrls = Boolean(newHMResult?.heatmapUrl) || some(newHMResult.nestedItems, 'heatmapUrl');
    if (hasUrls) {
      parsedHeatmaps.push(newHMResult);
    }
    yield;
  }
  return uniqBy(parsedHeatmaps, (heatmap) =>
    heatmap.overlaySection === ResultOverlaySection.SpotPositivity
      ? `${getHeatmapSourceIdentity(heatmap)}:${heatmap.columnName}`
      : heatmap
  );
}

export const parseParquetHeatmaps = (
  experimentResult: ExperimentResult,
  formatBracketsOptions: FormatBracketsOptions,
  isMultiStain: boolean,
  debug: boolean = false
): FeatureMetadataBeforeSecondaryAnalysisGrouping[] =>
  consumeParsingGenerator(parseParquetHeatmapsGenerator(experimentResult, formatBracketsOptions, isMultiStain, debug));

export function* createExperimentResultHeatmapParsingGenerator(
  experimentResult: ExperimentResult,
  formatBracketsOptions: Omit<FormatBracketsOptions, 'orchestrationId'>,
  isMultiStain: boolean,
  skipVectorHeatmaps = false
): Generator<void, FeatureMetadataBeforeSecondaryAnalysisGrouping[], void> {
  if (!experimentResult?.presentationInfo) {
    return [];
  }
  const legacyRasterHeatmaps = yield* parseLegacyRasterHeatmapsGenerator(experimentResult);

  const compositeRasterHeatmaps = yield* parseCompositeRasterHeatmapUrlsGenerator(experimentResult, {
    ...formatBracketsOptions,
    orchestrationId: experimentResult?.orchestrationId,
  });

  if (skipVectorHeatmaps) {
    return [...legacyRasterHeatmaps, ...compositeRasterHeatmaps];
  }

  // In old PMT results (first round of implementation) the heatmap is stored in the composite_layer_info key
  const legacyCompositePmtPresentationInfo = getPresentationInfoForOptionKey(experimentResult, legacyPmtCompositeKey);

  const legacyCompositePmt =
    legacyCompositePmtPresentationInfo && !isString(legacyCompositePmtPresentationInfo)
      ? yield* parseSinglePmtHeatmapGenerator({
          experimentResult,
          presentationInfo: legacyCompositePmtPresentationInfo,
          formatBracketsOptions: { ...formatBracketsOptions, orchestrationId: experimentResult?.orchestrationId },
          key: legacyPmtCompositeKey,
        })
      : null;

  const parquetHeatmaps = yield* parseParquetHeatmapsGenerator(
    experimentResult,
    { ...formatBracketsOptions, orchestrationId: experimentResult?.orchestrationId },
    isMultiStain
  );

  const pmtHeatmaps = yield* parsePmtHeatmapsGenerator(experimentResult, {
    ...formatBracketsOptions,
    orchestrationId: experimentResult?.orchestrationId,
  });

  const geoJsonHeatmaps: Array<FeatureMetadataBeforeSecondaryAnalysisGrouping | null> = [];
  for (const layerInfo of filter(flatten(values(experimentResult.presentationInfo)), { file_type: 'geojson' })) {
    geoJsonHeatmaps.push(
      parseGeoJsonHeatmap(experimentResult, layerInfo, {
        ...formatBracketsOptions,
        orchestrationId: experimentResult?.orchestrationId,
      })
    );
    yield;
  }

  const vectorHeatmaps = compact(concat([legacyCompositePmt], parquetHeatmaps, pmtHeatmaps, geoJsonHeatmaps));
  const allRasterHeatmaps = compact(concat(legacyRasterHeatmaps, compositeRasterHeatmaps));
  const allHeatmaps = compact(
    concat(
      vectorHeatmaps,
      experimentResult.flowClassName === FlowClassName.CalculateFeatures || isEmpty(vectorHeatmaps)
        ? allRasterHeatmaps
        : // Only show raster heatmaps that don't have a matching vector heatmap, since internal flows generate both types of heatmaps
          filter(
            allRasterHeatmaps,
            (heatmap) =>
              !find(vectorHeatmaps, { key: heatmap.key }) &&
              !find(vectorHeatmaps, { id: heatmap.id }) &&
              !find(vectorHeatmaps, { displayName: heatmap.displayName })
          )
    )
  );

  if ((!isEmpty(experimentResult.options) || !isEmpty(experimentResult.presentationInfo)) && isEmpty(allHeatmaps)) {
    console.warn('No heatmaps found in experiment result with options or presentation info', experimentResult);
  }

  return allHeatmaps;
}

export const parseExperimentResultHeatmaps = (
  experimentResult: ExperimentResult,
  formatBracketsOptions: Omit<FormatBracketsOptions, 'orchestrationId'>,
  isMultiStain: boolean,
  skipVectorHeatmaps = false
): FeatureMetadataBeforeSecondaryAnalysisGrouping[] =>
  consumeParsingGenerator(
    createExperimentResultHeatmapParsingGenerator(
      experimentResult,
      formatBracketsOptions,
      isMultiStain,
      skipVectorHeatmaps
    )
  );

export const parseExperimentResultHeatmapsWithPrimaryReuse = ({
  experimentResult,
  resultsByOrchestrationId,
  formatBracketsOptions,
  isMultiStain,
  skipVectorHeatmaps = false,
  parsingCache,
}: ExperimentResultHeatmapParsingOptions & {
  experimentResult: ExperimentResult;
  resultsByOrchestrationId: Map<string, ExperimentResult>;
}): ParsedExperimentResultHeatmaps => {
  const parseResult = (result: ExperimentResult) => {
    const cachedPrimaryPayload = parsingCache?.primaryPayloads.get(result);
    if (cachedPrimaryPayload) {
      parsingCache?.onPrimaryPayloadReused?.(result);
      return cachedPrimaryPayload;
    }
    const parsedHeatmaps = parseExperimentResultHeatmaps(
      result,
      formatBracketsOptions,
      isMultiStain,
      skipVectorHeatmaps
    );
    parsingCache?.onExperimentResultParsed?.(result);
    if (result.flowClassName === FlowClassName.CalculateFeatures) {
      parsingCache?.primaryPayloads.set(result, parsedHeatmaps);
      parsingCache?.onPrimaryPayloadParsed?.(result);
    }
    return parsedHeatmaps;
  };
  const primaryRunOrchestrationId = getPrimaryRunOrchestrationIdFromExperimentResult(experimentResult);
  const primaryResult = primaryRunOrchestrationId ? resultsByOrchestrationId.get(primaryRunOrchestrationId) : undefined;

  return {
    result: experimentResult,
    heatmaps: primaryResult
      ? composeSecondaryHeatmapsFromPrimary(parseResult(primaryResult), primaryResult, experimentResult)
      : parseResult(experimentResult),
  };
};

export const parseExperimentResultHeatmapsWithPrimaryReuseScheduled = async ({
  experimentResult,
  resultsByOrchestrationId,
  formatBracketsOptions,
  isMultiStain,
  skipVectorHeatmaps = false,
  parsingCache,
  scheduleGenerator,
}: ExperimentResultHeatmapParsingOptions & {
  experimentResult: ExperimentResult;
  resultsByOrchestrationId: Map<string, ExperimentResult>;
  scheduleGenerator: (
    generator: Generator<void, FeatureMetadataBeforeSecondaryAnalysisGrouping[], void>
  ) => Promise<FeatureMetadataBeforeSecondaryAnalysisGrouping[]>;
}): Promise<ParsedExperimentResultHeatmaps> => {
  const parseResult = async (result: ExperimentResult) => {
    const cachedPrimaryPayload = parsingCache?.primaryPayloads.get(result);
    if (cachedPrimaryPayload) {
      parsingCache?.onPrimaryPayloadReused?.(result);
      return cachedPrimaryPayload;
    }
    const parsedHeatmaps = await scheduleGenerator(
      createExperimentResultHeatmapParsingGenerator(result, formatBracketsOptions, isMultiStain, skipVectorHeatmaps)
    );
    parsingCache?.onExperimentResultParsed?.(result);
    if (result.flowClassName === FlowClassName.CalculateFeatures) {
      parsingCache?.primaryPayloads.set(result, parsedHeatmaps);
      parsingCache?.onPrimaryPayloadParsed?.(result);
    }
    return parsedHeatmaps;
  };
  const primaryRunOrchestrationId = getPrimaryRunOrchestrationIdFromExperimentResult(experimentResult);
  const primaryResult = primaryRunOrchestrationId ? resultsByOrchestrationId.get(primaryRunOrchestrationId) : undefined;

  return {
    result: experimentResult,
    heatmaps: primaryResult
      ? composeSecondaryHeatmapsFromPrimary(await parseResult(primaryResult), primaryResult, experimentResult)
      : await parseResult(experimentResult),
  };
};

export const groupParsedExperimentResultHeatmaps = (
  slideResults: ExperimentResult[],
  parsedResultHeatmaps: ParsedExperimentResultHeatmaps[]
): {
  publishedHeatmaps: FeatureMetadata[];
  publishableHeatmaps: { [key: string]: FeatureMetadata[] };
  internalHeatmaps: { [key: string]: FeatureMetadata[] };
  deletedHeatmaps: FeatureMetadata[];
} => {
  const allHeatmapsResults = flatMap(parsedResultHeatmaps, 'heatmaps');

  const allHeatmapsResultsWithNested = uniqBy(
    groupPrimaryResultsWithSecondaryResults(allHeatmapsResults),
    (heatmap) => {
      const resultIdentity = heatmap.orchestrationId || heatmap.experimentResultId;
      const segmentationIdentity = getCellSegmentationOverlayIdentity(heatmap);
      return segmentationIdentity
        ? `${resultIdentity}:${segmentationIdentity}`
        : heatmap.overlaySection === ResultOverlaySection.SpotPositivity
        ? `${resultIdentity}:${heatmap.overlaySection}:${heatmap.columnName}`
        : heatmap;
    }
  );

  const [deletedHeatmaps, heatmapsResults] = partition(allHeatmapsResultsWithNested, (result) =>
    Boolean(result.deletedAt)
  );

  const [publishedHeatmaps, allUnpublishedHeatmaps] = partition(heatmapsResults, (heatmap) => {
    const heatmapOrchestrationId = heatmap.orchestrationId;
    const runExperimentResults = heatmapOrchestrationId
      ? filter(
          slideResults,
          (result) =>
            result.flowClassName === FlowClassName.CalculateFeatures &&
            result.primaryRunOrchestrationId === heatmapOrchestrationId
        )
      : [];
    return (
      heatmap?.approved ||
      (heatmap.flowClassName === FlowClassName.CalculateFeatures && some(runExperimentResults, 'approved'))
    );
  });

  const visibleUnpublishedHeatmaps = filter(allUnpublishedHeatmaps, (heatmap) =>
    Boolean(experimentResultToGroup(heatmap))
  );
  const [publishableHeatmapsList, unpublishableHeatmapsList] = partition(
    visibleUnpublishedHeatmaps,
    (heatmap) =>
      includes(publishableFlowClasses, heatmap.flowClassName) || includes(publishableResultTypes, heatmap.resultType)
  );

  return {
    publishedHeatmaps,
    publishableHeatmaps: groupBy(publishableHeatmapsList, experimentResultToGroup),
    internalHeatmaps: groupBy(unpublishableHeatmapsList, experimentResultToGroup),
    deletedHeatmaps,
  };
};

export const parseHeatmaps = (
  slideResults: ExperimentResult[],
  formatBracketsOptions: Omit<FormatBracketsOptions, 'orchestrationId'>,
  isMultiStain: boolean,
  skipVectorHeatmaps = false,
  parsingCache?: HeatmapParsingCache,
  parsedResultHeatmaps?: ParsedExperimentResultHeatmaps[]
): {
  publishedHeatmaps: FeatureMetadata[];
  publishableHeatmaps: { [key: string]: FeatureMetadata[] };
  internalHeatmaps: { [key: string]: FeatureMetadata[] };
  deletedHeatmaps: FeatureMetadata[];
} => {
  const resultsByOrchestrationId = new Map(slideResults.map((result) => [result.orchestrationId, result] as const));
  return groupParsedExperimentResultHeatmaps(
    slideResults,
    parsedResultHeatmaps ||
      map(slideResults, (experimentResult) =>
        parseExperimentResultHeatmapsWithPrimaryReuse({
          experimentResult,
          resultsByOrchestrationId,
          formatBracketsOptions,
          isMultiStain,
          skipVectorHeatmaps,
          parsingCache,
        })
      )
  );
};
